import type { TourProgressAdapter } from '@klonup/design-system';
import { LocalStorageTourProgressAdapter } from './LocalStorageTourProgressAdapter';
import { OnboardingHttpRepository } from './infrastructure/OnboardingHttpRepository';

export class BackendTourProgressAdapter implements TourProgressAdapter {
  private seen = new Set<string>();
  private completed = new Set<string>();
  private fallback = new LocalStorageTourProgressAdapter();
  private backendOk = true;
  /**
   * Resuelve cuando la precarga del progreso desde el backend termina (ok o fallo).
   * El consumidor (OrgLayout) la espera antes de decidir el auto-disparo de un tour, para
   * no preguntar `hasSeen` con los Sets aún vacíos (race: el tour reaparecía siempre).
   */
  private readonly readyPromise: Promise<void>;

  constructor(private readonly repo: OnboardingHttpRepository) {
    // Sembrar desde localStorage (síncrono): durante la ventana de carga del backend ya hay
    // un valor razonable, y queda coherente con el fallback si el backend falla.
    for (const id of this.fallback.snapshotSeen()) {
      this.seen.add(id);
    }
    for (const id of this.fallback.snapshotCompleted()) {
      this.completed.add(id);
    }

    // Precarga desde backend (fuente de verdad); si falla, degradar a localStorage.
    this.readyPromise = this.repo
      .getProgress()
      .then(({ seenTourIds, completedTourIds }) => {
        seenTourIds.forEach((id) => this.seen.add(id));
        completedTourIds.forEach((id) => {
          this.completed.add(id);
          this.seen.add(id); // completado implica visto
        });
      })
      .catch(() => {
        this.backendOk = false;
      });
  }

  /** Espera a que la precarga inicial del progreso haya terminado (no rechaza nunca). */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  hasCompleted(tourId: string): boolean {
    if (!this.backendOk) return this.fallback.hasCompleted(tourId);
    return this.completed.has(tourId);
  }

  hasSeen(tourId: string): boolean {
    if (!this.backendOk) return this.fallback.hasSeen(tourId);
    return this.seen.has(tourId) || this.completed.has(tourId);
  }

  markSeen(tourId: string): void {
    this.seen.add(tourId);
    this.fallback.markSeen(tourId); // espejo local siempre
    if (this.backendOk) {
      this.repo.markSeen(tourId).catch(() => {
        this.backendOk = false;
      });
    }
  }

  markCompleted(tourId: string): void {
    this.completed.add(tourId);
    this.seen.add(tourId); // completado implica visto
    this.fallback.markCompleted(tourId); // espejo local siempre
    if (this.backendOk) {
      this.repo.markCompleted(tourId).catch(() => {
        this.backendOk = false;
      });
    }
  }

  reset(): void {
    this.seen.clear();
    this.completed.clear();
    this.fallback.reset();
    if (this.backendOk) {
      this.repo.reset().catch(() => {
        this.backendOk = false;
      });
    }
  }
}
